import { useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonMenuButton,
  IonButtons,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsStore } from '@/store/data.store';
import KpiTile from '@/components/KpiTile';
import Sparkline from '@/components/Sparkline';
import StatusPill from '@/components/StatusPill';
import PlatformChip from '@/components/PlatformChip';
import EmptyState from '@/components/EmptyState';
import { listOutline } from 'ionicons/icons';

const numberFormat = new Intl.NumberFormat('en');

export default function DashboardPage() {
  const { t } = useTranslation();
  const dashboard = useAnalyticsStore((s) => s.dashboard);
  const loading = useAnalyticsStore((s) => s.loading);
  const days = useAnalyticsStore((s) => s.days);
  const loadDashboard = useAnalyticsStore((s) => s.loadDashboard);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const totalsMap = Object.fromEntries((dashboard?.totals ?? []).map((entry) => [entry.key, entry.total]));
  const engagementsSeries = (dashboard?.series ?? []).map((point) => point.engagements);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>{t('dashboard.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={(event) => { void loadDashboard().then(() => event.detail.complete()); }}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && !dashboard ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : !dashboard ? (
          <EmptyState icon={listOutline} title={t('common.noResults')} />
        ) : (
          <div className="page-shell">
            <div className="kpi-grid">
              <KpiTile label={t('dashboard.engagement')} value={numberFormat.format(totalsMap.engagements ?? 0)} />
              <KpiTile label={t('dashboard.reach')} value={numberFormat.format(totalsMap.reach ?? 0)} />
              <KpiTile label={t('dashboard.impressions')} value={numberFormat.format(totalsMap.impressions ?? 0)} />
              <KpiTile label={t('dashboard.scheduledPosts')} value={numberFormat.format(dashboard.statusCounts.scheduled ?? 0)} />
              <KpiTile label={t('dashboard.totalPosts')} value={numberFormat.format(dashboard.statusCounts.posted ?? 0)} />
              <KpiTile label={t('dashboard.queuedPosts')} value={numberFormat.format(dashboard.statusCounts.queued ?? 0)} />
            </div>

            <h6 className="section-title">{t('dashboard.performance')} · {days}d</h6>
            {engagementsSeries.length > 1 ? (
              <Sparkline points={engagementsSeries} height={80} />
            ) : (
              <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>{t('common.noResults')}</p>
            )}

            <h6 className="section-title">{t('dashboard.channels')}</h6>
            {dashboard.platformBreakdown.length === 0 ? (
              <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>{t('accounts.connectFirst')}</p>
            ) : (
              dashboard.platformBreakdown.map((row) => (
                <div key={row.platformId} className="dash-row">
                  <PlatformChip platform={row.platformId} />
                  <div className="dash-row__track">
                    <div
                      className="dash-row__fill"
                      style={{
                        width: `${Math.min(100, (row.engagements / Math.max(1, dashboard.platformBreakdown[0].engagements)) * 100)}%`,
                      }}
                    />
                  </div>
                  <small>{numberFormat.format(row.engagements)}</small>
                </div>
              ))
            )}

            <h6 className="section-title">{t('dashboard.topPosts')}</h6>
            {dashboard.topPosts.length === 0 ? (
              <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>{t('content.noPosts')}</p>
            ) : (
              dashboard.topPosts.map((post) => (
                <div key={post.id} className="dash-row" style={{ padding: '6px 0', borderBottom: '1px solid rgba(100,116,139,0.12)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {post.title ?? '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                      {post.platformIds.map((id) => <PlatformChip key={id} platform={id} />)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong>{numberFormat.format(post.engagements)}</strong>
                    <div><StatusPill status="posted" /></div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}
